import { ApiError } from './error'
import type {
  AuthMe,
  AuthProvider,
  CartLine,
  VoucherStatus,
  Nachweis,
  CartView,
  CheckoutRequest,
  Collection,
  CollectionPage,
  Fulfilment,
  Order,
  OrderLine,
  PageLink,
  PaymentMethod,
  PaymentStart,
  PaymentStatus,
  Product,
  ProductList,
  ProductQuery,
  ProductSummary,
  ProductVariant,
  ShopConfig,
  ShopPage,
} from './types'

// Ein kleiner Satz Beispieldaten, damit die Seiten ansehbar sind, bevor die
// Schnittstelle steht. Bewusst nicht hübsch, sondern unbequem: fehlende
// Kombinationen, eine ausverkaufte Variante, ein Produkt ganz ohne Achsen und
// ein sehr langer Name. Ein Laden aus lauter Idealfällen zeigt nicht, was
// schiefgeht.

/**
 * Platzhalterbilder als data:-URI.
 *
 * Kein Verweis nach außen: Die Entwicklung soll auch ohne Netz funktionieren,
 * und ein fremder Bilddienst wäre ein Datenabfluss aus dem Laden eines Kunden.
 */
function placeholder(label: string, hue: number): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600">
<rect width="600" height="600" fill="hsl(${hue} 30% 88%)"/>
<circle cx="300" cy="250" r="120" fill="hsl(${hue} 35% 78%)"/>
<text x="300" y="500" font-family="system-ui,sans-serif" font-size="34"
 fill="hsl(${hue} 30% 32%)" text-anchor="middle">${label.replace(/[<>&]/g, '')}</text>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/**
 * Ein breites Bild für die Bühne und die Sammlungskacheln.
 *
 * Auch das als data:-URI und nicht als Datei: Der Probebetrieb soll ohne Netz
 * und ohne Auslieferungsordner funktionieren. Gemalt wird nichts Konkretes —
 * ein Verlauf mit zwei Formen und ein Horizont reichen, um zu beurteilen, ob
 * die Schrift auf dem Bild lesbar bleibt. Genau das ist die Frage, die ein
 * Platzhalter beantworten muss.
 */
function szene(hue: number, breite = 1600, hoehe = 900): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${breite} ${hoehe}">
<defs>
<linearGradient id="g" x1="0" y1="0" x2="0.4" y2="1">
<stop offset="0" stop-color="hsl(${hue} 34% 74%)"/>
<stop offset="1" stop-color="hsl(${(hue + 40) % 360} 28% 46%)"/>
</linearGradient>
</defs>
<rect width="${breite}" height="${hoehe}" fill="url(#g)"/>
<circle cx="${breite * 0.72}" cy="${hoehe * 0.3}" r="${hoehe * 0.26}" fill="hsl(${hue} 40% 84%)" opacity="0.55"/>
<path d="M0 ${hoehe * 0.72} Q ${breite * 0.3} ${hoehe * 0.58} ${breite * 0.55} ${hoehe * 0.7} T ${breite} ${hoehe * 0.66} V ${hoehe} H 0 Z" fill="hsl(${(hue + 20) % 360} 26% 34%)" opacity="0.5"/>
<path d="M0 ${hoehe * 0.84} Q ${breite * 0.35} ${hoehe * 0.74} ${breite * 0.62} ${hoehe * 0.86} T ${breite} ${hoehe * 0.8} V ${hoehe} H 0 Z" fill="hsl(${(hue + 20) % 360} 24% 24%)" opacity="0.55"/>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/** Muss zu shippingFlatGross im Server passen — siehe unten. */
const VERSANDPAUSCHALE = 4.99

/**
 * Die Freigrenze dieses Beispielladens.
 *
 * Der Server kennt sie als Einstellung je Laden (ShippingFreeFrom). Hier steht
 * eine, weil „Noch 8,40 € bis zum kostenlosen Versand" die wirksamste Zeile im
 * ganzen Bezahlvorgang ist — ein Probebetrieb, der sie nie zeigt, lässt genau
 * die Stelle ungeprüft, an der sich entscheidet, ob jemand noch etwas dazulegt.
 */
const VERSANDFREI_AB = 50

/**
 * Die Länder, in die dieser Beispielladen liefert.
 *
 * Nur Deutschland — wie die Vorgabe des Servers, wenn ein Laden nichts
 * eingestellt hat. Damit zeigt der Probebetrieb auch den Fall „Hinderungsgrund":
 * Wer in der Kasse Österreich wählt, bekommt den Satz des Servers zu sehen.
 */
const LIEFERLAENDER = ['DE']

/**
 * Ein Logo als data:-URI, wie das übrige Bildmaterial der Beispieldaten.
 *
 * Damit ist im Probebetrieb auch der Weg mit Logo zu sehen und nicht nur der
 * mit Ladennamen — die Kopfzeile sieht mit Bild anders aus, und das fiele
 * sonst erst beim ersten Kunden auf, der eines hochlädt.
 */
const LOGO = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 48">
<rect x="1" y="1" width="46" height="46" rx="8" fill="#2f5d7c"/>
<text x="24" y="33" font-family="Georgia,serif" font-size="26" fill="#ffffff" text-anchor="middle">M</text>
<text x="60" y="33" font-family="Georgia,serif" font-size="24" fill="#2f5d7c">Laden Müller</text>
</svg>`,
)}`

const FAVICON = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
<rect width="48" height="48" rx="8" fill="#2f5d7c"/>
<text x="24" y="34" font-family="Georgia,serif" font-size="28" fill="#ffffff" text-anchor="middle">M</text>
</svg>`,
)}`

const config: ShopConfig = {
  shopName: 'Laden Müller',
  currency: 'EUR',
  primaryHost: 'laden-mueller.de',
  shippingFlatGross: VERSANDPAUSCHALE,
  shippingFreeFrom: VERSANDFREI_AB,
  pickupEnabled: true,
  // Bewusst nicht die Vorlagenwerte: Ein Probebetrieb in den Vorgabefarben
  // würde nie zeigen, ob das Aussehen überhaupt ankommt. Die Schriftfolgen
  // stehen genauso vollständig da, wie der Server sie schickt — er gibt die
  // Kennung „serif" nie heraus, sondern immer die fertige Folge.
  theme: {
    accent: '#2f5d7c',
    accentText: '#ffffff',
    accentDark: '#8ec3e6',
    // Vom Server ausgerechnet: die lesbare Schrift auf dem hellen Akzent des
    // Dunkelmodus. Hier mitgeführt, damit der Probebetrieb dieselbe Form hat.
    accentTextDark: '#1c1a17',
    radius: 10,
    fontHeading: 'Georgia, "Times New Roman", serif',
    fontBody: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    logoUrl: LOGO,
    faviconUrl: FAVICON,
    tagline: 'Regionales aus dem Allgäu, seit 1961.',
    footerNote: 'Laden Müller e. K. · Marktstraße 3 · 87435 Kempten',
    // Mit Bühne, weil der Probebetrieb sonst genau den Teil nicht zeigt, der
    // aus einem Verzeichnis einen Laden macht. Wie es ohne aussieht, lässt
    // sich hier durch Leeren von heroUrl in einem Atemzug nachsehen.
    heroUrl: szene(205),
    heroHeadline: 'Was hier wächst, steht hier im Regal',
    heroSubline:
      'Saft, Kaffee, Wolle und Steingut aus dem Allgäu — von Höfen und Werkstätten, die wir kennen.',
    heroCtaLabel: 'Sortiment ansehen',
    heroCtaHref: '/suche',
    promises: ['Versand in 24 Stunden', 'Ab 50 € versandkostenfrei', 'Abholung im Laden möglich'],
  },
}

// ---------- Seiten ----------
//
// Die Pflichtseiten, die der Server beim Anlegen eines Ladens vorschlägt, und
// eine freiwillige. Der Text nutzt absichtlich jede Auszeichnung, die das
// Ladenfenster beherrscht — und eine, die es nicht beherrschen darf: In der
// Widerrufsbelehrung steht spitze Klammer für spitze Klammer ein Stück HTML.
// Wenn es im Browser als Text erscheint, ist die Darstellung dicht; erscheint
// es als Kasten oder gar nicht, ist sie es nicht.

const pages: ShopPage[] = [
  {
    handle: 'impressum',
    title: 'Impressum',
    body: `## Angaben gemäß § 5 DDG

Laden Müller e. K.
Marktstraße 3
87435 Kempten

**Vertreten durch:** Anna Müller

## Kontakt

- Telefon: 0831 123456
- E-Mail: [post@laden-mueller.de](mailto:post@laden-mueller.de)

Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: *DE123456789*`,
  },
  {
    handle: 'datenschutz',
    title: 'Datenschutz',
    body: `Wir erheben nur, was für die Bestellung nötig ist.

## Welche Daten

1. Name und Anschrift, damit die Ware ankommt
2. E-Mail-Adresse, für die Bestätigung
3. Telefonnummer, falls der Bote nicht klingelt

## Wie lange

Bestelldaten bleiben zehn Jahre, so lange verlangt es das Handelsrecht. Alles
Weitere löschen wir, sobald der Grund entfällt.

Fragen dazu beantwortet [Anna Müller](mailto:post@laden-mueller.de).`,
  },
  {
    handle: 'widerruf',
    title: 'Widerrufsbelehrung',
    body: `## Widerrufsrecht

Sie haben das Recht, binnen **vierzehn Tagen** ohne Angabe von Gründen diesen
Vertrag zu widerrufen.

Die Frist beginnt an dem Tag, an dem Sie die Ware in Besitz genommen haben.

## Folgen des Widerrufs

- Wir erstatten alle Zahlungen, auch die Lieferkosten der günstigsten Art
- Die Rücksendung tragen Sie
- Wertersatz nur, wenn die Ware über das Übliche hinaus benutzt wurde

Eine Zeile mit Markup: <script>alert('nicht ausführen')</script> — sie muss als
Text dastehen.

Für den Widerruf genügt eine formlose Nachricht an
[post@laden-mueller.de](mailto:post@laden-mueller.de).`,
  },
  {
    handle: 'versand',
    title: 'Versand und Zahlung',
    body: `## Versand

Wir versenden innerhalb Deutschlands für **4,99 €**. Bei Abholung im Laden
entfallen die Versandkosten.

## Zahlung

Bezahlt wird nach der Bestellung. Was der Laden annimmt, steht auf der
Bezahlseite.

Mehr zur Rückgabe steht in der [Widerrufsbelehrung](/seite/widerruf).`,
  },
]

// Drei Sammlungen mit Bild, eine ohne: Die vierte ist der Grund, aus dem es
// die getönte Fläche gibt. Ein Probebetrieb, in dem jede Sammlung ein Bild
// hat, zeigt nie, wie die Reihe aussieht, wenn eine fehlt — und fehlen wird
// eines beim ersten echten Laden.
const collections: Collection[] = [
  {
    id: 'c1',
    handle: 'getraenke',
    name: 'Getränke',
    description: 'Saft, Kaffee und Wasser aus der Region.',
    imageUrl: szene(35, 900, 1100),
  },
  {
    id: 'c2',
    handle: 'kleidung',
    name: 'Kleidung',
    description: 'Gestrickt und genäht, in kleinen Mengen.',
    imageUrl: szene(340, 900, 1100),
  },
  {
    id: 'c3',
    handle: 'haushalt',
    name: 'Haushalt',
    description: 'Was man täglich braucht und selten sucht.',
    imageUrl: szene(150, 900, 1100),
  },
  {
    id: 'c4',
    handle: 'geschenke',
    name: 'Geschenke',
    description: 'Kleinigkeiten zum Mitbringen.',
  },
]

interface MockProduct extends Product {
  collections: string[]
}

const products: MockProduct[] = [
  {
    id: 'p1',
    handle: 'roter-pullover',
    name: 'Roter Pullover',
    description:
      'Ein dicker Pullover aus Schurwolle, gestrickt in einer kleinen Manufaktur im Allgäu. Er kratzt am ersten Tag ein wenig und danach nie wieder.',
    seoTitle: 'Roter Pullover aus Schurwolle',
    seoDescription:
      'Dicker Schurwollpullover in Rot und Blau, Größen S bis L — gestrickt im Allgäu.',
    images: [
      { url: placeholder('Pullover, Vorderseite', 0), altText: 'Roter Wollpullover von vorne' },
      { url: placeholder('Pullover, Rückseite', 10), altText: 'Roter Wollpullover von hinten' },
      { url: placeholder('Strickmuster', 20), altText: 'Nahaufnahme des Zopfmusters am Ärmel' },
    ],
    variantAxes: ['Größe', 'Farbe'],
    variants: [
      { id: 'v1', axes: { 'Größe': 'S', 'Farbe': 'rot' }, price: 89.9, available: true, stockQuantity: 4 },
      { id: 'v2', axes: { 'Größe': 'M', 'Farbe': 'rot' }, price: 89.9, available: true, stockQuantity: 2 },
      // Ausverkauft, aber vorhanden: Die Kombination existiert, sie ist nur
      // gerade nicht lieferbar — das ist etwas anderes als „gibt es nicht".
      { id: 'v3', axes: { 'Größe': 'L', 'Farbe': 'rot' }, price: 94.9, available: false, stockQuantity: 0 },
      // „S/blau" fehlt absichtlich: so lässt sich die gesperrte Kombination sehen.
      { id: 'v4', axes: { 'Größe': 'M', 'Farbe': 'blau' }, price: 89.9, available: true, stockQuantity: 7 },
      { id: 'v5', axes: { 'Größe': 'L', 'Farbe': 'blau' }, price: 94.9, available: true, stockQuantity: 1 },
    ],
    collections: ['kleidung', 'geschenke'],
  },
  {
    id: 'p2',
    handle: 'apfelsaft-naturtrueb',
    name: 'Apfelsaft naturtrüb',
    description:
      'Gepresst aus Streuobst von Wiesen im Umkreis von zwanzig Kilometern. Ungefiltert, deshalb setzt sich unten etwas ab — einmal schwenken.',
    seoTitle: 'Naturtrüber Apfelsaft aus Streuobst',
    seoDescription: 'Ungefilterter Apfelsaft aus regionalem Streuobst, in 0,5 l und 1 l.',
    images: [
      { url: placeholder('Apfelsaft', 40), altText: 'Flasche naturtrüber Apfelsaft' },
      { url: placeholder('Streuobstwiese', 90), altText: 'Streuobstwiese mit alten Apfelbäumen' },
    ],
    variantAxes: ['Größe'],
    variants: [
      { id: 'v6', axes: { 'Größe': '0,5 l' }, price: 2.4, available: true, stockQuantity: 48 },
      { id: 'v7', axes: { 'Größe': '1 l' }, price: 3.9, available: true, stockQuantity: 23 },
    ],
    collections: ['getraenke'],
  },
  {
    id: 'p3',
    handle: 'espressotassen-set',
    name: 'Espressotassen, Set aus vier Stück',
    description: 'Steingut, im Ofen gebrannt, spülmaschinenfest. Jede Tasse ist etwas anders.',
    seoTitle: 'Espressotassen aus Steingut, 4er-Set',
    seoDescription: 'Handgetöpfertes Espressotassen-Set aus Steingut, spülmaschinenfest.',
    images: [{ url: placeholder('Espressotassen', 200), altText: 'Vier Espressotassen aus Steingut nebeneinander' }],
    // Ohne Achsen: ein Produkt, eine Variante. Die Auswahl muss dann verschwinden.
    variantAxes: [],
    variants: [{ id: 'v8', axes: {}, price: 38, available: true, stockQuantity: 6 }],
    collections: ['haushalt', 'geschenke'],
  },
  {
    id: 'p4',
    handle: 'bio-kaffee-ganze-bohne',
    name: 'Bio-Kaffee, ganze Bohne',
    description: 'Mittlere Röstung, in der Rösterei zwei Straßen weiter geröstet. Schokolade, wenig Säure.',
    seoTitle: 'Bio-Kaffee ganze Bohne, mittlere Röstung',
    seoDescription: 'Regional gerösteter Bio-Kaffee in 250 g und 1 kg.',
    images: [{ url: placeholder('Kaffeebohnen', 30), altText: 'Geröstete Kaffeebohnen in einer Schale' }],
    variantAxes: ['Gewicht', 'Mahlgrad'],
    variants: [
      { id: 'v9', axes: { 'Gewicht': '250 g', 'Mahlgrad': 'ganze Bohne' }, price: 9.8, available: true, stockQuantity: 15 },
      { id: 'v10', axes: { 'Gewicht': '250 g', 'Mahlgrad': 'gemahlen' }, price: 9.8, available: true, stockQuantity: 9 },
      { id: 'v11', axes: { 'Gewicht': '1 kg', 'Mahlgrad': 'ganze Bohne' }, price: 34.5, available: true, stockQuantity: 3 },
      // „1 kg gemahlen" gibt es nicht — gemahlen wird nur in kleinen Mengen.
    ],
    collections: ['getraenke'],
  },
  {
    id: 'p5',
    handle: 'wollsocken',
    name: 'Wollsocken',
    description: 'Zwei Paar, ungefärbt. Halten warm, auch wenn sie feucht sind.',
    seoTitle: 'Ungefärbte Wollsocken, Doppelpack',
    seoDescription: 'Ungefärbte Wollsocken im Doppelpack, Größen 36 bis 46.',
    images: [{ url: placeholder('Wollsocken', 60), altText: 'Zwei Paar ungefärbte Wollsocken' }],
    variantAxes: ['Größe'],
    variants: [
      { id: 'v12', axes: { 'Größe': '36–38' }, price: 18.5, available: true, stockQuantity: 5 },
      { id: 'v13', axes: { 'Größe': '39–42' }, price: 18.5, available: false, stockQuantity: 0 },
      { id: 'v14', axes: { 'Größe': '43–46' }, price: 19.5, available: true, stockQuantity: 2 },
    ],
    collections: ['kleidung'],
  },
  {
    id: 'p6',
    handle: 'handseife-lavendel',
    name: 'Handseife Lavendel',
    description: 'Kaltgerührt, ohne Palmöl. Der Duft kommt aus dem Öl, nicht aus dem Labor.',
    seoTitle: 'Kaltgerührte Handseife mit Lavendel',
    seoDescription: 'Handseife aus kaltgerührter Produktion, ohne Palmöl, mit Lavendelöl.',
    images: [{ url: placeholder('Handseife', 280), altText: 'Handseife mit Lavendel auf einer Holzschale' }],
    variantAxes: [],
    variants: [{ id: 'v15', axes: {}, price: 6.5, available: true, stockQuantity: 31 }],
    collections: ['haushalt'],
  },
  {
    id: 'p7',
    handle: 'mineralwasser-kasten',
    name: 'Mineralwasser, Kasten mit zwölf Flaschen',
    description: 'Aus der Quelle im Nachbarort. Pfand ist im Preis nicht enthalten.',
    seoTitle: 'Mineralwasser aus regionaler Quelle, 12er-Kasten',
    seoDescription: 'Kasten Mineralwasser mit zwölf Glasflaschen, still oder mit Kohlensäure.',
    images: [{ url: placeholder('Mineralwasser', 190), altText: 'Kasten mit zwölf Glasflaschen Mineralwasser' }],
    variantAxes: ['Sorte'],
    variants: [
      { id: 'v16', axes: { 'Sorte': 'still' }, price: 7.9, available: true, stockQuantity: 12 },
      { id: 'v17', axes: { 'Sorte': 'medium' }, price: 7.9, available: true, stockQuantity: 8 },
      { id: 'v18', axes: { 'Sorte': 'spritzig' }, price: 7.9, available: true, stockQuantity: 0 },
    ],
    collections: ['getraenke'],
  },
  {
    id: 'p8',
    handle: 'leinenschuerze',
    name: 'Leinenschürze',
    description: 'Grobes Leinen, doppelt vernäht an den Bändern. Wird mit jedem Waschen weicher.',
    seoTitle: 'Leinenschürze, doppelt vernäht',
    seoDescription: 'Schürze aus grobem Leinen in Natur und Anthrazit.',
    images: [{ url: placeholder('Leinenschürze', 150), altText: 'Leinenschürze an einem Haken' }],
    variantAxes: ['Farbe'],
    variants: [
      { id: 'v19', axes: { 'Farbe': 'natur' }, price: 42, available: true, stockQuantity: 4 },
      { id: 'v20', axes: { 'Farbe': 'anthrazit' }, price: 42, available: true, stockQuantity: 6 },
    ],
    collections: ['kleidung', 'haushalt', 'geschenke'],
  },
]

/** Etwas Verzögerung, damit die Ladezustände im Alltag sichtbar sind. */
function delayed<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), 180))
}

function summarize(product: MockProduct): ProductSummary {
  const prices = product.variants.map((v) => v.price)
  return {
    id: product.id,
    handle: product.handle,
    name: product.name,
    imageUrl: product.images[0]?.url ?? '',
    // Das zweite Bild fürs Überfahren. Der echte Server schickt es (noch)
    // nicht; im Probebetrieb steht es, weil sich der Wechsel sonst nirgends
    // ansehen lässt.
    hoverImageUrl: product.images[1]?.url,
    priceFrom: prices.length > 0 ? Math.min(...prices) : 0,
    available: product.variants.some((v) => v.available),
    // Wie im Server: nur bei genau einer Variante, und nur, wenn sie kaufbar
    // ist. Sonst rät die Kachel eine Größe.
    quickAddVariantId:
      product.variants.length === 1 && product.variants[0]!.available
        ? product.variants[0]!.id
        : undefined,
  }
}

export function getConfig(): Promise<ShopConfig> {
  // Die Zahlungsarten kommen erst hier dazu, damit es nur eine Liste gibt: Der
  // Server bildet sie ebenfalls aus derselben Quelle wie getPaymentMethods,
  // für den Versandfall.
  return delayed({ ...config, paymentMethods: zahlungsartenFuer('shipping') })
}

export function getPages(): Promise<PageLink[]> {
  return delayed(pages.map(({ handle, title }) => ({ handle, title })))
}

export function getPage(handle: string): Promise<ShopPage> {
  const seite = pages.find((p) => p.handle === handle)
  if (!seite) return Promise.reject(new ApiError(404, 'Diese Seite gibt es nicht.'))
  return delayed(seite)
}

export function getCollections(): Promise<Collection[]> {
  return delayed(collections)
}

export function getCollection(handle: string): Promise<CollectionPage> {
  const collection = collections.find((c) => c.handle === handle)
  if (!collection) return Promise.reject(new ApiError(404, 'Sammlung nicht gefunden'))
  return delayed({
    collection,
    products: products.filter((p) => p.collections.includes(handle)).map(summarize),
  })
}

export function getProducts(params: ProductQuery): Promise<ProductList> {
  const term = params.search?.trim().toLowerCase() ?? ''
  let found = products
  if (params.collection) found = found.filter((p) => p.collections.includes(params.collection!))
  if (term) {
    found = found.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        p.variants.some((v) => Object.values(v.axes).some((x) => x.toLowerCase().includes(term))),
    )
  }

  const pageSize = params.pageSize ?? 24
  const page = Math.max(1, params.page ?? 1)
  const start = (page - 1) * pageSize
  return delayed({
    products: found.slice(start, start + pageSize).map(summarize),
    total: found.length,
  })
}

export function getProduct(handle: string): Promise<Product> {
  const product = products.find((p) => p.handle === handle)
  if (!product) return Promise.reject(new ApiError(404, 'Produkt nicht gefunden'))
  const { collections: _zugehoerigkeit, ...rest } = product
  return delayed(rest)
}

// ---------- Warenkorb, Kasse, Bestellung ----------
//
// Die Beispieldaten bestellen wirklich: Sie halten einen Warenkorb, buchen den
// Bestand ab und legen die Bestellung ab, damit die Nachschau etwas findet.
// Nur im Speicher — ein Neuladen der Seite leert alles.
//
// Das ist ein Unterschied zum Server, und der einzige, der sich nicht
// vermeiden lässt: Dort liegt der Warenkorb in der Datenbank und überlebt das
// Neuladen, hier gibt es keine. Alle *Formen* sind dieselben, und alle Regeln
// auch — Kürzen bei knappem Bestand, Freigrenze, Hinderungsgründe. Ein
// Probebetrieb, der sich anders verhält als das echte System, führt beim
// Entwickeln in die Irre.

const bestellungen = new Map<string, { order: Order; email: string; token: string }>()
let laufendeNummer = 1000

// --- Die Ablage des Probebetriebs -----------------------------------------
//
// Sie steht für die Datenbank des Servers, nicht für einen Speicher im
// Browser — der Unterschied ist wichtig.
//
// Der Warenkorb liegt beim Server, und dass er ein Neuladen überlebt, ist
// genau die Eigenschaft, um die es bei dieser Umstellung geht. Ein
// Probebetrieb, in dem er jedes Mal leer wäre, zeigte ausgerechnet das nicht
// und ließe eine kaputte Fassung durchgehen. Deshalb liegt hier, was der
// Server sich merken würde: Warenkorb, Anmeldung, Bestellungen,
// Zahlungsanforderungen und der abgebuchte Bestand.
//
// sessionStorage und nicht localStorage: Ein neuer Tab ist ein neuer Besucher
// mit leerem Korb, und zum Aufräumen genügt es, den Tab zu schließen.
const ABLAGE = 'ahri-shop-probebetrieb-v1'

interface Ablage {
  warenkorb: [string, number][]
  bestellungen: [string, { order: Order; email: string; token: string }][]
  laufendeNummer: number
  angemeldet: AuthMe
  zahlungen: [string, { methode: string; seit: number; abgebrochen: boolean }][]
  bestand: [string, { menge: number; lieferbar: boolean }][]
}

function sichern(): void {
  try {
    const inhalt: Ablage = {
      warenkorb: [...warenkorb.entries()],
      bestellungen: [...bestellungen.entries()],
      laufendeNummer,
      angemeldet,
      zahlungen: [...zahlungen.entries()],
      bestand: products.flatMap((produkt) =>
        produkt.variants.map(
          (variante) =>
            [variante.id, { menge: variante.stockQuantity, lieferbar: variante.available }] as [
              string,
              { menge: number; lieferbar: boolean },
            ],
        ),
      ),
    }
    window.sessionStorage.setItem(ABLAGE, JSON.stringify(inhalt))
  } catch {
    /* Kein Speicher — dann hält der Probebetrieb eben nur bis zum Neuladen. */
  }
}

function laden(): void {
  try {
    const roh = window.sessionStorage.getItem(ABLAGE)
    if (!roh) return
    const inhalt = JSON.parse(roh) as Ablage
    for (const [variantId, menge] of inhalt.warenkorb ?? []) warenkorb.set(variantId, menge)
    for (const [nummer, eintrag] of inhalt.bestellungen ?? []) bestellungen.set(nummer, eintrag)
    for (const [nummer, zahlung] of inhalt.zahlungen ?? []) zahlungen.set(nummer, zahlung)
    if (typeof inhalt.laufendeNummer === 'number') laufendeNummer = inhalt.laufendeNummer
    if (inhalt.angemeldet) angemeldet = inhalt.angemeldet
    for (const [variantId, stand] of inhalt.bestand ?? []) {
      const treffer = findVariant(variantId)
      if (!treffer) continue
      treffer.variant.stockQuantity = stand.menge
      treffer.variant.available = stand.lieferbar
    }
  } catch {
    // Eine unbekannte Form: lieber ein frischer Probebetrieb als einer, der an
    // seinen eigenen alten Daten abstürzt.
  }
}

/** Nachschlagen einer Variante über alle Produkte — der Warenkorb kennt nur ihre Kennung. */
function findVariant(variantId: string): { product: MockProduct; variant: ProductVariant } | null {
  for (const product of products) {
    const variant = product.variants.find((v) => v.id === variantId)
    if (variant) return { product, variant }
  }
  return null
}

function variantLabel(product: MockProduct, variant: ProductVariant): string {
  const werte = Object.values(variant.axes)
  return werte.length > 0 ? `${product.name} (${werte.join(' · ')})` : product.name
}

/** Die Achsenwerte, nach Achsennamen sortiert — genau wie der Server sie schreibt. */
function achsentext(variant: ProductVariant): string {
  return Object.keys(variant.axes)
    .sort()
    .map((achse) => variant.axes[achse])
    .filter(Boolean)
    .join(' · ')
}

/** Die Seife wird nicht bestandsgeführt — sie zeigt den Fall „unbegrenzt". */
function unbegrenzt(product: MockProduct): boolean {
  return product.handle === 'handseife-lavendel'
}

function runde2(wert: number): number {
  return Math.round(wert * 100) / 100
}

// --- Warenkorb -------------------------------------------------------------
//
// Auf dem „Server": eine Abbildung von Variantenkennung auf Menge, in der
// Reihenfolge des Hineinlegens. Preise stehen nicht darin — sie werden bei
// jedem Anzeigen frisch gerechnet, wie im Server.

const warenkorb = new Map<string, number>()

/** Wie shop.ClampQuantity im Server: kürzen, nicht ablehnen. */
function begrenze(gewuenscht: number, verfuegbar: number | null): number {
  if (!Number.isFinite(gewuenscht) || gewuenscht <= 0) return 0
  let menge = Math.min(Math.floor(gewuenscht), 999)
  if (verfuegbar !== null && menge > verfuegbar) {
    return verfuegbar <= 0 ? 0 : verfuegbar
  }
  return menge
}

/**
 * Stellt die Ansicht zusammen und räumt dabei auf.
 *
 * Was nicht mehr lieferbar ist, wird gekürzt oder markiert — hier, beim
 * Ansehen, nicht erst an der Kasse. Genau wie shopcart.Build im Server, samt
 * Festschreiben der gekürzten Menge: Sonst zeigte die Seite zwei, der
 * Warenkorb wüsste von fünf, und an der Kasse gewönne der Warenkorb.
 */
/**
 * Die Rabattaktionen des Probebetriebs.
 *
 * Genug Vielfalt, um die Faelle zu treffen, die im Server auseinandergehen:
 * Prozent und Festbetrag, mit und ohne Mindestbestellwert, auf den ganzen
 * Einkauf und auf eine Warengruppe. Ein Probebetrieb mit genau einem
 * Prozentrabatt wuerde die Verteilung ueber mehrere Steuersaetze nie ausloesen.
 */
const RABATTE: Record<string, {
  name: string
  art: 'percent' | 'amount'
  wert: number
  mindestwert?: number
  /** Leer heisst: der ganze Einkauf. */
  handles?: string[]
  abgelaufen?: boolean
}> = {
  WILLKOMMEN10: { name: '10 % Willkommensrabatt', art: 'percent', wert: 10 },
  FUENFEURO: { name: '5 € geschenkt', art: 'amount', wert: 5, mindestwert: 30 },
  KAFFEE20: { name: '20 % auf Kaffee', art: 'percent', wert: 20, handles: ['bio-kaffee'] },
  SOMMER: { name: 'Sommeraktion', art: 'percent', wert: 15, abgelaufen: true },
}

/** Der eingetragene Rabattcode. Liegt beim Server, nicht im Browser. */
let rabattcode = ''

/**
 * Was ein Rabattcode an diesem Korb bewirkt.
 *
 * Dieselbe Rechnung wie bl/discount: gedeckelt auf die Grundlage, anteilig am
 * Brutto verteilt, Rest aus dem Runden auf die groesste Zeile. Die Saetze zu
 * den Problemen sind wortgleich mit denen des Servers — eine abweichende
 * Fassung hier wuerde verdecken, dass dort etwas anderes steht.
 */
function rechneRabatt(
  zeilen: CartLine[],
  zwischensumme: number,
  code: string,
): { gesamt: number; name: string; problem: string } {
  if (code === '') return { gesamt: 0, name: '', problem: '' }

  const regel = RABATTE[code]
  if (!regel) return { gesamt: 0, name: '', problem: 'Diesen Rabattcode gibt es nicht.' }
  if (regel.abgelaufen) return { gesamt: 0, name: regel.name, problem: 'Diese Aktion ist beendet.' }

  if (regel.mindestwert != null && zwischensumme < regel.mindestwert) {
    return {
      gesamt: 0,
      name: regel.name,
      problem: `Dieser Rabattcode gilt ab einem Einkaufswert von ${regel.mindestwert.toFixed(2).replace('.', ',')} €.`,
    }
  }

  const betroffen = regel.handles
    ? zeilen.filter((z) => regel.handles!.includes(z.handle))
    : zeilen
  const grundlage = runde2(betroffen.reduce((summe, z) => summe + z.totalGross, 0))
  if (grundlage <= 0) {
    return {
      gesamt: 0,
      name: regel.name,
      problem: 'Dieser Rabattcode gilt für keinen Artikel in Ihrem Warenkorb.',
    }
  }

  let gesamt = regel.art === 'percent' ? runde2((grundlage * regel.wert) / 100) : runde2(regel.wert)
  // Gedeckelt: Ein Festbetrag über dem Einkaufswert ergäbe sonst einen
  // negativen Preis. Ein Rabattcode ist kein Guthaben — der Rest verfällt.
  if (gesamt > grundlage) gesamt = grundlage
  if (gesamt <= 0) return { gesamt: 0, name: regel.name, problem: '' }

  // Anteilig verteilen, damit sich die Aufteilung im Probebetrieb genauso
  // verhält wie im Server. Der Rest aus dem Runden geht auf die größte Zeile,
  // sonst stimmt die Summe der Zeilen nicht mit dem ausgewiesenen Rabatt.
  let verteilt = 0
  let groesste: CartLine | null = null
  for (const zeile of betroffen) {
    const anteil = runde2((gesamt * zeile.totalGross) / grundlage)
    zeile.discountGross = anteil
    verteilt = runde2(verteilt + anteil)
    if (groesste == null || zeile.totalGross > groesste.totalGross) groesste = zeile
  }
  const rest = runde2(gesamt - verteilt)
  if (rest !== 0 && groesste != null) {
    groesste.discountGross = runde2((groesste.discountGross ?? 0) + rest)
  }

  return { gesamt, name: regel.name, problem: '' }
}

function baueAnsicht(fulfilment: Fulfilment, country: string): CartView {
  const pickup = fulfilment === 'pickup'
  const zeilen: CartLine[] = []
  let anzahl = 0
  let zwischensumme = 0

  for (const [variantId, gewuenscht] of [...warenkorb.entries()]) {
    const treffer = findVariant(variantId)
    if (!treffer || (!treffer.variant.available && treffer.variant.stockQuantity <= 0 && !unbegrenzt(treffer.product))) {
      // Nicht mehr im Schaufenster. Die Zeile bleibt sichtbar, damit der Kunde
      // weiß, was fehlt.
      zeilen.push({
        lineId: `l-${variantId}`,
        variantId,
        handle: treffer?.product.handle ?? '',
        name: treffer ? treffer.product.name : '',
        variant: treffer ? achsentext(treffer.variant) : '',
        imageUrl: treffer?.product.images[0]?.url ?? '',
        quantity: gewuenscht,
        unitGross: 0,
        totalGross: 0,
        available: 0,
        adjusted: false,
        unavailable: true,
      })
      continue
    }

    const { product, variant } = treffer
    const frei = unbegrenzt(product) ? null : variant.stockQuantity
    const menge = begrenze(gewuenscht, frei)

    const zeile: CartLine = {
      lineId: `l-${variantId}`,
      variantId,
      handle: product.handle,
      name: product.name,
      variant: achsentext(variant),
      imageUrl: product.images[0]?.url ?? '',
      quantity: menge,
      unitGross: variant.price,
      totalGross: runde2(variant.price * menge),
      available: frei,
      adjusted: menge !== gewuenscht,
      unavailable: menge <= 0,
    }

    // Die gekürzte Menge gleich festschreiben — wie im Server.
    if (zeile.adjusted) {
      if (menge <= 0) warenkorb.delete(variantId)
      else warenkorb.set(variantId, menge)
    }

    zeilen.push(zeile)
    anzahl += menge
    zwischensumme = runde2(zwischensumme + zeile.totalGross)
  }

  // Der Rabatt, mit derselben Rechnung wie bl/discount im Server: anteilig am
  // Brutto auf die Zeilen, damit jede Steuergrundlage im richtigen Verhältnis
  // sinkt. Der Probebetrieb rechnet das mit, obwohl hier keine Steuer
  // ausgewiesen wird — eine einfachere Rechnung hier hiesse, dass sich eine
  // falsche Verteilung im Server nicht zeigt.
  const rabatt = rechneRabatt(zeilen, zwischensumme, rabattcode)

  // Versand und Mindestbestellwert messen den Betrag NACH Rabatt — wie im
  // Server. Sonst liesse sich jede Freigrenze mit einem Code unterlaufen.
  const nachRabatt = runde2(zwischensumme - rabatt.gesamt)

  const versand = pickup || (VERSANDFREI_AB > 0 && nachRabatt >= VERSANDFREI_AB)
    ? 0
    : VERSANDPAUSCHALE
  const fehltZurFreigrenze =
    pickup || VERSANDFREI_AB <= 0 || nachRabatt >= VERSANDFREI_AB
      ? 0
      : runde2(VERSANDFREI_AB - nachRabatt)

  // Dieselben Sätze wie shop.CheckoutBlockers im Server, Wort für Wort: Das
  // Ladenfenster zeigt sie unverändert an, und eine abweichende Fassung im
  // Probebetrieb würde genau das verdecken.
  const blockers: string[] = []
  if (!pickup && !LIEFERLAENDER.includes(country.trim().toUpperCase())) {
    blockers.push('Dieser Laden liefert nicht in das gewählte Land.')
  }

  return {
    lines: zeilen,
    count: anzahl,
    subtotalGross: zwischensumme,
    discountCode: rabattcode,
    discountName: rabatt.name,
    discountGross: rabatt.gesamt,
    discountProblem: rabatt.problem,
    shippingGross: versand,
    freeShippingMissing: fehltZurFreigrenze,
    totalGross: runde2(nachRabatt + versand),
    currency: 'EUR',
    blockers,
  }
}

export function getCart(fulfilment: Fulfilment, country: string): Promise<CartView> {
  const ansicht = baueAnsicht(fulfilment, country)
  sichern()
  return delayed(ansicht)
}

export function setCartLine(
  variantId: string,
  quantity: number,
  fulfilment: Fulfilment,
  country: string,
): Promise<CartView> {
  const treffer = findVariant(variantId)
  if (!treffer) {
    return Promise.reject(new ApiError(400, 'Diesen Artikel gibt es hier nicht.'))
  }

  const frei = unbegrenzt(treffer.product) ? null : treffer.variant.stockQuantity
  const menge = begrenze(quantity, frei)
  if (menge <= 0) warenkorb.delete(variantId)
  else warenkorb.set(variantId, menge)

  const ansicht = baueAnsicht(fulfilment, country)
  sichern()
  return delayed(ansicht)
}

export function clearCart(fulfilment: Fulfilment, country: string): Promise<CartView> {
  warenkorb.clear()
  const ansicht = baueAnsicht(fulfilment, country)
  sichern()
  return delayed(ansicht)
}

// --- Anmeldung -------------------------------------------------------------

const anbieter: AuthProvider[] = [{ key: 'ahri', label: 'Hirax Cloud' }]

let angemeldet: AuthMe = { signedIn: false }

export function getAuthProviders(): Promise<AuthProvider[]> {
  return delayed(anbieter)
}

export function getMe(): Promise<AuthMe> {
  return delayed(angemeldet)
}

export function logout(): Promise<unknown> {
  angemeldet = { signedIn: false }
  // Wie im Server: Das Warenkorb-Cookie wird mit abgeräumt, der Abgemeldete
  // bekommt also einen leeren Korb. Sein bisheriger gehört jetzt seinem Konto.
  warenkorb.clear()
  sichern()
  return delayed({ ok: true })
}

/**
 * Die Anmeldung im Probebetrieb.
 *
 * Hier weicht der Probebetrieb ab, und zwar unvermeidlich: Der Server leitet
 * zu einem echten Anbieter weiter, und den gibt es ohne Netz nicht. Statt eine
 * Anmeldemaske nachzubauen, die es so nirgends gibt, meldet der Probebetrieb
 * direkt an und kehrt nach `returnTo` zurück — dieselbe Bewegung, nur ohne den
 * Umweg. Alles danach (angemeldeter Zustand, Bestellliste, Abmelden) verhält
 * sich wie am Server.
 */
export async function startLogin(_key: string, returnTo: string): Promise<void> {
  await delayed(null)
  angemeldet = { signedIn: true, name: 'Anna Beispiel', email: 'anna@example.org' }
  sichern()
  // Ein vollständiger Seitenwechsel wie beim Server — die Anwendung entsteht
  // danach neu und fragt alles frisch ab.
  window.location.assign(returnTo || '/')
}

export function getMyOrders(): Promise<Order[]> {
  if (!angemeldet.signedIn) {
    return Promise.reject(new ApiError(401, 'Dafür müssen Sie angemeldet sein.'))
  }
  const meine = [...bestellungen.values()]
    .filter((eintrag) => eintrag.email === (angemeldet.email ?? '').toLowerCase())
    .map((eintrag) => eintrag.order)
    .reverse()
  return delayed(meine)
}

// --- Zahlungsarten ---------------------------------------------------------
//
// Dieselben Kennungen, Beschriftungen, Hinweise und Reihenfolgen wie in
// bl/paymethod und den Verfahren daneben. Wer sie hier anders schreibt, prüft
// im Probebetrieb eine Kasse, die es nicht gibt.

const ZAHLUNGSARTEN: PaymentMethod[] = [
  {
    key: 'ahriPay',
    label: 'Hirax Pay',
    hint: 'Mit der Hirax-App einen Code scannen. Ohne Konto in diesem Laden.',
    kind: 'qr',
    requiresPickup: false,
    position: 10,
  },
  {
    key: 'sumup',
    label: 'Karte',
    hint: 'Sie werden zur Bezahlseite von SumUp geleitet und kommen danach zurück.',
    kind: 'redirect',
    requiresPickup: false,
    position: 20,
  },
  {
    key: 'prepayment',
    label: 'Vorkasse',
    hint: 'Sie erhalten die Bankverbindung mit der Bestellbestätigung.',
    kind: 'manual',
    requiresPickup: false,
    position: 30,
  },
  {
    key: 'cashOnPickup',
    label: 'Barzahlung bei Abholung',
    hint: 'Sie zahlen im Laden, wenn Sie die Ware abholen.',
    kind: 'manual',
    requiresPickup: true,
    position: 40,
  },
]

function zahlungsartenFuer(fulfilment: Fulfilment): PaymentMethod[] {
  const pickup = fulfilment === 'pickup'
  return ZAHLUNGSARTEN.filter((art) => !art.requiresPickup || pickup)
}

export function getPaymentMethods(fulfilment: Fulfilment): Promise<PaymentMethod[]> {
  return delayed(zahlungsartenFuer(fulfilment))
}

// --- Bestellen -------------------------------------------------------------

export function postCheckout(
  daten: CheckoutRequest,
): Promise<{ bestellung: Order; token: string }> {
  const ansicht = baueAnsicht(
    daten.fulfilment,
    daten.address?.country ?? 'DE',
  )

  // Die Hinderungsgründe auch hier, obwohl die Kasse sie schon zeigt: Ein
  // Rumpf kann alles behaupten, und der Mindestbestellwert ist keine Bitte.
  if (ansicht.blockers.length > 0) {
    return Promise.reject(new ApiError(400, ansicht.blockers[0]!))
  }

  const bestellbar = ansicht.lines.filter((zeile) => !zeile.unavailable && zeile.quantity > 0)
  if (bestellbar.length === 0) {
    return Promise.reject(new ApiError(400, 'Der Warenkorb ist leer.'))
  }

  const zeilen: OrderLine[] = []
  for (const zeile of bestellbar) {
    const treffer = findVariant(zeile.variantId)
    if (!treffer) {
      return Promise.reject(
        new ApiError(409, 'Ein Artikel im Warenkorb ist nicht mehr im Sortiment. Bitte entfernen Sie ihn.'),
      )
    }

    const { product, variant } = treffer
    if (!unbegrenzt(product) && (!variant.available || variant.stockQuantity < zeile.quantity)) {
      // Die Meldung nennt den Artikel und die Zahl — „Fehler beim Bestellen"
      // sagt niemandem, welche Zeile er ändern soll.
      return Promise.reject(
        new ApiError(
          409,
          variant.stockQuantity > 0
            ? `Von ${variantLabel(product, variant)} sind noch ${variant.stockQuantity} verfügbar.`
            : `${variantLabel(product, variant)} ist ausverkauft.`,
        ),
      )
    }

    zeilen.push({
      name: product.name,
      variant: achsentext(variant),
      // Die Variante reist mit, damit „Noch einmal bestellen" weiß, was es in
      // den Korb legen soll — und das Bild, damit eine Wochen später geöffnete
      // Bestellung wiedererkennbar bleibt.
      variantId: variant.id,
      imageUrl: product.images[0]?.url ?? '',
      quantity: zeile.quantity,
      unitGross: variant.price,
      totalGross: zeile.totalGross,
    })
  }

  // Erst nach der vollständigen Prüfung abbuchen: Eine halb gebuchte
  // Bestellung wäre schlimmer als eine abgelehnte.
  for (const zeile of bestellbar) {
    const treffer = findVariant(zeile.variantId)
    if (!treffer || unbegrenzt(treffer.product)) continue
    treffer.variant.stockQuantity = Math.max(0, treffer.variant.stockQuantity - zeile.quantity)
    if (treffer.variant.stockQuantity === 0) treffer.variant.available = false
  }

  laufendeNummer += 1
  const order: Order = {
    id: `o${laufendeNummer}`,
    number: `B-${laufendeNummer}`,
    subtotalGross: ansicht.subtotalGross,
    // Die Versandkosten aus derselben Rechnung wie die Ansicht — mit
    // Freigrenze. Eine Zahl aus dem Rumpf wäre eine, die der Kunde selbst
    // bestimmt hat.
    shippingGross: ansicht.shippingGross,
    totalGross: ansicht.totalGross,
    totalTax: runde2(ansicht.totalGross - ansicht.totalGross / 1.19),
    currency: 'EUR',
    fulfilment: daten.fulfilment,
    customerName: daten.name,
    // Bei Abholung ausdrücklich `null` und kein leeres Objekt: Es gibt keine
    // Anschrift, an die etwas ginge, und die Bestellansicht sagt das lieber,
    // als eine leere zu zeigen.
    shippingAddress: daten.address ?? null,
    // Die Rechnung entsteht im Server erst mit der Zahlung. Ein Knopf, der
    // vorher ein PDF verspricht, führt in eine Fehlerseite.
    hasInvoice: false,
    // Wie im Server: Eine frische Bestellung wartet auf die Zahlung, gleich ob
    // sie versandt oder abgeholt wird. Ein Probebetrieb, der sie sofort als
    // bestätigt führt, zeigt die Bezahlseite nie — und die ist der Teil, an dem
    // sich entscheidet, ob der Laden Geld einnimmt.
    status: 'awaiting_payment',
    createdAt: new Date().toISOString(),
    lines: zeilen,
  }

  // Der Warenkorb ist aufgebraucht. Ihn stehen zu lassen hieße, dem Kunden
  // nach der Bestellung dieselbe Ware noch einmal anzubieten.
  warenkorb.clear()

  // Ein Schluessel wie im Echtbetrieb — sonst zeigte der Probebetrieb einen
  // Weg, den es dort nicht gibt.
  const token = zufallsschluessel()
  bestellungen.set(order.number.toLowerCase(), {
    order,
    email: daten.email.trim().toLowerCase(),
    token,
  })
  sichern()
  return delayed({ bestellung: order, token })
}

/** Ein Schluessel in der Form, die auch der Server liefert. */
function zufallsschluessel(): string {
  const roh = new Uint8Array(24)
  crypto.getRandomValues(roh)
  return btoa(String.fromCharCode(...roh)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function getOrder(nummer: string, nachweis: { token?: string; email?: string }): Promise<Order> {
  const eintrag = bestellungen.get(nummer.trim().toLowerCase())
  // Falsche E-Mail und unbekannte Nummer ergeben dieselbe Antwort: Wer die
  // Nummer durchprobiert, soll nicht daran ablesen können, welche es gibt.
  // Der Schluessel zaehlt wie im Echtbetrieb: Wer ihn hat, darf sehen. Sonst
  // die E-Mail. Ein Probebetrieb, der hier grosszuegiger waere als der Server,
  // liesse eine kaputte Fassung durchgehen.
  const passt =
    eintrag != null &&
    ((nachweis.token != null && nachweis.token !== '' && nachweis.token === eintrag.token) ||
      (nachweis.email != null &&
        eintrag.email === nachweis.email.trim().toLowerCase()))
  if (!passt) {
    return Promise.reject(new ApiError(404, 'Zu dieser Nummer und E-Mail-Adresse gibt es keine Bestellung.'))
  }
  return delayed(eintrag.order)
}

// --- Bezahlen --------------------------------------------------------------
//
// Nachgebildet, nicht vereinfacht: Die Zahlung wird nicht sofort bezahlt,
// sondern erst nach ein paar Sekunden. Ein Probebetrieb, in dem sofort
// „bezahlt" steht, zeigt die Wartesituation nie — und genau die ist der Teil,
// an dem sich im Echtbetrieb entscheidet, ob die Seite etwas taugt.
//
// Die manuellen Arten bleiben dagegen offen, so lange man will. Das ist keine
// Lücke, sondern ihr ganzer Punkt: Der Laden bucht sie, wenn das Geld da ist.

const zahlungen = new Map<
  string,
  { methode: string; seit: number; abgebrochen: boolean }
>()

/** Nach so vielen Sekunden gilt eine angeforderte Beispielzahlung als bezahlt. */
const ZAHLT_NACH_MS = 6000

export async function startPayment(
  nummer: string,
  _nachweis: Nachweis,
  methode: string,
): Promise<PaymentStart> {
  await delayed(null)

  const art = ZAHLUNGSARTEN.find((eintrag) => eintrag.key === methode)
  if (!art) {
    return Promise.reject(new ApiError(400, `Zahlungsart "${methode}" kennt dieser Server nicht`))
  }

  const bestellung = bestellungen.get(nummer.toLowerCase())?.order
  if (bestellung?.paidAt) {
    return { kind: art.kind, payUrl: '', qrUrl: '', amount: 0, currency: 'EUR',
             note: '', recipient: '', expiresAt: '', state: 'paid' }
  }

  // Eine offene Anforderung derselben Art wiederverwenden — wie im Server:
  // Zwei bezahlbare Anforderungen über denselben Betrag könnte der Kunde beide
  // bezahlen.
  const vorhanden = zahlungen.get(nummer)
  const zahlung =
    vorhanden && !vorhanden.abgebrochen && vorhanden.methode === methode
      ? vorhanden
      : { methode, seit: Date.now(), abgebrochen: false }
  zahlungen.set(nummer, zahlung)
  sichern()

  const betrag = bestellung?.totalGross ?? 0
  const gemeinsam = {
    amount: betrag,
    currency: 'EUR',
    expiresAt: new Date(zahlung.seit + 10 * 60 * 1000).toISOString(),
    state: 'pending' as const,
  }

  switch (art.kind) {
    case 'qr':
      return {
        ...gemeinsam,
        kind: 'qr',
        payUrl: `https://auth.ahri.cloud/wallet/transfer?token=tok-${nummer}`,
        qrUrl: qrPlatzhalter(),
        note: '',
        recipient: 'Laden Müller',
      }
    case 'redirect':
      return {
        ...gemeinsam,
        kind: 'redirect',
        payUrl: 'https://pay.sumup.com/b2c/BEISPIEL',
        qrUrl: '',
        note: '',
        recipient: 'SumUp',
      }
    default:
      return {
        ...gemeinsam,
        kind: 'manual',
        payUrl: '',
        qrUrl: '',
        // Derselbe Satz, den manualProvider im Server mitbringt, wenn der Laden
        // keinen eigenen hinterlegt hat.
        note:
          methode === 'cashOnPickup'
            ? 'Bitte zahlen Sie bei der Abholung im Laden. Wir legen die Ware für Sie zurück.'
            : 'Bitte überweisen Sie den Betrag unter Angabe der Bestellnummer. Die Ware wird versandt, sobald die Zahlung eingegangen ist.',
        recipient: '',
      }
  }
}

export async function getPaymentStatus(nummer: string, _nachweis: Nachweis): Promise<PaymentStatus> {
  await delayed(null)
  const zahlung = zahlungen.get(nummer)
  const bestellung = bestellungen.get(nummer.toLowerCase())?.order

  if (!zahlung || zahlung.abgebrochen) {
    return {
      payment: { state: 'none', paid: false, method: '', canRetry: true },
      order: bestellung,
    }
  }

  const art = ZAHLUNGSARTEN.find((eintrag) => eintrag.key === zahlung.methode)
  // Manuelle Arten gehen nie von allein auf „bezahlt": Es gibt niemanden zu
  // fragen. Der Laden bucht sie im Backoffice, wenn das Geld da ist.
  const bezahlt = art?.kind !== 'manual' && Date.now() - zahlung.seit > ZAHLT_NACH_MS

  if (bezahlt && bestellung) {
    bestellung.status = 'paid'
    bestellung.paidAt = new Date().toISOString()
    // Erst jetzt gibt es eine Rechnung — vorher stünde der Knopf da und
    // führte in eine Fehlerseite.
    bestellung.hasInvoice = true
    sichern()
  }

  return {
    payment: {
      state: bezahlt ? 'paid' : 'pending',
      paid: bezahlt,
      method: zahlung.methode,
      canRetry: false,
    },
    order: bestellung,
  }
}

export async function cancelPayment(nummer: string, _nachweis: Nachweis): Promise<unknown> {
  await delayed(null)
  const zahlung = zahlungen.get(nummer)
  if (zahlung) zahlung.abgebrochen = true
  sichern()
  return { status: 'canceled' }
}

/**
 * Im Probebetrieb ein gezeichneter Platzhalter statt eines echten Codes.
 *
 * Ein QR-Code, der auf eine Zahlung zeigt, die es nicht gibt, wäre schlimmer
 * als ein Kasten mit Beschriftung: Wer ihn scannt, landet in einem Fehler und
 * sucht ihn dann im falschen System.
 */
function qrPlatzhalter(): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512">
    <rect width="512" height="512" fill="#fff"/>
    <rect x="16" y="16" width="480" height="480" fill="none" stroke="#111" stroke-width="8"/>
    <text x="256" y="248" text-anchor="middle" font-family="sans-serif" font-size="28" fill="#111">QR-Code</text>
    <text x="256" y="288" text-anchor="middle" font-family="sans-serif" font-size="20" fill="#666">Beispieldaten</text>
  </svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/**
 * Eine fertige Beispielbestellung — zugestellt, mit Sendung und Anschrift.
 *
 * Ohne sie zeigt der Probebetrieb die Bestellansicht nie so, wie ein Kunde sie
 * meistens sieht: Was hier durch die Kasse geht, wartet auf die Zahlung, hat
 * keine Sendungsnummer, keinen Zustellzeitpunkt und keine Rechnung. Genau die
 * Teile, über die beim Ansehen entschieden wird, fehlten also — und eine
 * Ansicht, die man nicht ansehen kann, wird schief.
 *
 * Sie gehört Anna Beispiel, also derselben Kennung, die `startLogin` anmeldet.
 * So steht sie auch unter „Meine Bestellungen" und nicht nur in der Nachschau.
 */
function beispielbestellung(): void {
  const tageHer = (tage: number, stunde: number) => {
    const wert = new Date()
    wert.setDate(wert.getDate() - tage)
    wert.setHours(stunde, 20, 0, 0)
    return wert.toISOString()
  }

  const pullover = findVariant('v4')
  const kaffee = findVariant('v9')
  const tassen = findVariant('v8')
  const posten = [
    { treffer: pullover, menge: 1 },
    { treffer: kaffee, menge: 2 },
    { treffer: tassen, menge: 1 },
  ].filter((eintrag) => eintrag.treffer)

  const zeilen: OrderLine[] = posten.map(({ treffer, menge }) => ({
    name: treffer!.product.name,
    variant: achsentext(treffer!.variant),
    variantId: treffer!.variant.id,
    imageUrl: treffer!.product.images[0]?.url ?? '',
    quantity: menge,
    unitGross: treffer!.variant.price,
    totalGross: runde2(treffer!.variant.price * menge),
  }))

  const zwischensumme = runde2(zeilen.reduce((summe, zeile) => summe + zeile.totalGross, 0))
  const versand = zwischensumme >= VERSANDFREI_AB ? 0 : VERSANDPAUSCHALE
  const gesamt = runde2(zwischensumme + versand)

  const order: Order = {
    id: 'o1042',
    number: 'B-1042',
    subtotalGross: zwischensumme,
    shippingGross: versand,
    totalGross: gesamt,
    totalTax: runde2(gesamt - gesamt / 1.19),
    currency: 'EUR',
    status: 'delivered',
    fulfilment: 'shipping',
    customerName: 'Anna Beispiel',
    hasInvoice: true,
    lines: zeilen,
    shippingAddress: {
      name: 'Anna Beispiel',
      company: '',
      street: 'Maximilianstraße 12',
      extra: 'Hinterhaus, 2. Stock',
      postalCode: '86150',
      city: 'Augsburg',
      country: 'DE',
    },
    shipments: [
      {
        carrier: 'dhl',
        carrierName: 'DHL',
        trackingCode: '00340434161094042557',
        trackingUrl: 'https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html',
        status: 'delivered',
        shippedAt: tageHer(3, 17),
        deliveredAt: tageHer(1, 11),
      },
    ],
    createdAt: tageHer(5, 9),
    paidAt: tageHer(5, 9),
    shippedAt: tageHer(3, 17),
    deliveredAt: tageHer(1, 11),
  }

  bestellungen.set(order.number.toLowerCase(), { order, email: 'anna@example.org', token: 'probe-schluessel-1042' })
}

// Ganz zum Schluss, wenn alles steht: den gemerkten Stand einlesen. Weiter
// oben ginge es nicht — die Abbildungen und die Beispieldaten gibt es dann
// noch nicht.
//
// Die Beispielbestellung davor, nicht danach: Was in der Ablage liegt, ist
// jünger und soll gewinnen — sonst setzte ein Neuladen eine bezahlte
// Bestellung wieder auf „zugestellt vor einem Tag" zurück.
beispielbestellung()
laden()


/**
 * Einen Rabattcode eintragen oder entfernen.
 *
 * Wie im Server: Der Code wird nur gemerkt. Ob er greift, entscheidet sich bei
 * jedem Anzeigen neu — eine Aktion kann enden, waehrend der Warenkorb steht.
 */
export function setCartDiscount(
  code: string,
  fulfilment: Fulfilment,
  country: string,
): Promise<CartView> {
  rabattcode = code.trim().toUpperCase()
  sichern()
  return delayed(baueAnsicht(fulfilment, country))
}

/**
 * Die Wertgutscheine des Probebetriebs.
 *
 * Drei Faelle, weil sie sich im Laden verschieden anfuehlen: einer mit vollem
 * Guthaben, einer fast leer (dort bleibt ein Rest zu zahlen) und ein
 * abgelaufener.
 */
const GUTSCHEINE: Record<string, { guthaben: number; abgelaufen?: boolean }> = {
  AHRI34679ACD: { guthaben: 50 },
  AHRIQRTUVWXY: { guthaben: 7.5 },
  AHRIMNPQRTUV: { guthaben: 25, abgelaufen: true },
}

export function checkVoucher(code: string): Promise<VoucherStatus> {
  const sauber = code.trim().toUpperCase().replace(/[^0-9A-Z]/g, '')
  const g = GUTSCHEINE[sauber]
  if (!g) {
    return Promise.reject(new ApiError(404, 'Diesen Gutschein gibt es nicht.'))
  }
  if (g.abgelaufen) {
    return delayed({
      code: sauber,
      balance: g.guthaben,
      currency: 'EUR',
      usable: false,
      expiresAt: '2025-12-31T23:59:59Z',
      reason: 'Dieser Gutschein ist am 31.12.2025 abgelaufen.',
    })
  }
  return delayed({
    code: sauber,
    balance: g.guthaben,
    currency: 'EUR',
    usable: true,
    expiresAt: null,
    reason: '',
  })
}
